import {
    BUILDER_IMAGE_DEFAULT_CROP_POSITION,
    BUILDER_IMAGE_ORIGIN,
    BUILDER_IMAGE_PATH_PREFIX,
    getBuilderImageAspectHeight,
} from "./builderImageUrl";

const BUILDER_IMAGE_PROXY_PATH = "/api/builder-image";

function isBuilderImageUrl(url: string): boolean {
    try {
        const parsedUrl = new URL(url);
        return (
            parsedUrl.origin === BUILDER_IMAGE_ORIGIN &&
            parsedUrl.pathname.startsWith(BUILDER_IMAGE_PATH_PREFIX)
        );
    } catch {
        return false;
    }
}

function isBuilderImageProxyUrl(url: string): boolean {
    return url.startsWith(`${BUILDER_IMAGE_PROXY_PATH}?`);
}

export function toFirstPartyBuilderImageUrl(url: string | undefined | null): string | undefined {
    if (!url) return undefined;

    if (isBuilderImageProxyUrl(url) || !isBuilderImageUrl(url)) {
        return url;
    }

    const params = new URLSearchParams({ url });
    return `${BUILDER_IMAGE_PROXY_PATH}?${params.toString()}`;
}

/**
 * Formats an image URL by appending query parameters.
 * Safely handles URLs that already contain query parameters and preserves fragments.
 *
 * Builder CDN image URLs are rewritten through the first-party image proxy so
 * Chrome does not report third-party cookie issues for public images.
 *
 * @param url - The image URL to format (can be null/undefined)
 * @param params - Query parameters to append (default: 'format=webp')
 * @returns Formatted URL string, or undefined if url is falsy (to prevent empty src causing CLS)
 */
export function formatImageUrl(
    url: string | undefined | null,
    params: string = "format=webp",
): string | undefined {
    return formatImageUrlWithProxyMode(url, params, true);
}

function formatImageUrlWithProxyMode(
    url: string | undefined | null,
    params: string,
    useBuilderProxy: boolean,
): string | undefined {
    if (!url) return undefined;

    // Handle fragments
    const fragmentIndex = url.indexOf("#");
    const fragment = fragmentIndex !== -1 ? url.slice(fragmentIndex) : "";
    const baseUrl = fragmentIndex !== -1 ? url.slice(0, fragmentIndex) : url;
    const proxiedBaseUrl = useBuilderProxy
        ? (toFirstPartyBuilderImageUrl(baseUrl) ?? baseUrl)
        : baseUrl;

    // Use & if URL already has query params, otherwise use ?
    const separator = proxiedBaseUrl.includes("?") ? "&" : "?";

    return `${proxiedBaseUrl}${separator}${params}${fragment}`;
}

export function formatDirectImageUrl(
    url: string | undefined | null,
    params: string = "format=webp",
): string | undefined {
    return formatImageUrlWithProxyMode(url, params, false);
}

function formatDirectImageUrlWithOverriddenParams(
    url: string | undefined | null,
    params: string,
): string | undefined {
    if (!url) return undefined;

    try {
        const absoluteUrlPattern = /^[a-z][a-z\d+\-.]*:/i;
        const isAbsoluteUrl = absoluteUrlPattern.test(url);
        const parsedUrl = new URL(url, isAbsoluteUrl ? undefined : "https://skywire.local");

        for (const [key, value] of new URLSearchParams(params)) {
            parsedUrl.searchParams.set(key, value);
        }

        if (isAbsoluteUrl) {
            return parsedUrl.toString();
        }

        return `${parsedUrl.pathname}${parsedUrl.search}${parsedUrl.hash}`;
    } catch {
        return formatDirectImageUrl(url, params);
    }
}

export function formatBuilderImageUrl(
    url: string | undefined | null,
    width: number,
    params: string = "format=webp",
): string | undefined {
    return formatImageUrl(url, `${params}&width=${width}`);
}

function formatFixedAspectBuilderImageParams(
    width: number,
    aspectRatio: number,
    params: string,
    position: string = BUILDER_IMAGE_DEFAULT_CROP_POSITION,
): string {
    const searchParams = new URLSearchParams(params);

    searchParams.set("format", "webp");
    searchParams.set("width", String(width));
    searchParams.set("height", String(getBuilderImageAspectHeight(width, aspectRatio)));
    searchParams.set("fit", "cover");
    searchParams.set("position", position);

    return searchParams.toString();
}

export function formatBuilderImageUrlForAspectRatio(
    url: string | undefined | null,
    width: number,
    aspectRatio: number,
    params: string = "format=webp",
    position: string = BUILDER_IMAGE_DEFAULT_CROP_POSITION,
): string | undefined {
    return formatImageUrl(
        url,
        formatFixedAspectBuilderImageParams(width, aspectRatio, params, position),
    );
}

function formatImageSrcsetWithFormatter(
    url: string | undefined | null,
    widths: readonly number[],
    formatter: (url: string, width: number, params: string) => string | undefined,
    params: string,
): string | undefined {
    if (!url) return undefined;

    const uniqueWidths = [...new Set(widths)].filter((width) => width > 0).sort((a, b) => a - b);

    if (uniqueWidths.length === 0) {
        return undefined;
    }

    const candidates = uniqueWidths
        .map((width) => {
            const formattedUrl = formatter(url, width, params);
            return formattedUrl ? `${formattedUrl} ${width}w` : undefined;
        })
        .filter(Boolean);

    return candidates.length ? candidates.join(", ") : undefined;
}

export function formatBuilderImageSrcset(
    url: string | undefined | null,
    widths: readonly number[],
    params: string = "format=webp",
): string | undefined {
    return formatImageSrcsetWithFormatter(url, widths, formatBuilderImageUrl, params);
}

export function formatBuilderImageSrcsetForAspectRatio(
    url: string | undefined | null,
    widths: readonly number[],
    aspectRatio: number,
    params: string = "format=webp",
    position: string = BUILDER_IMAGE_DEFAULT_CROP_POSITION,
): string | undefined {
    return formatImageSrcsetWithFormatter(
        url,
        widths,
        (imageUrl, width, imageParams) =>
            formatBuilderImageUrlForAspectRatio(
                imageUrl,
                width,
                aspectRatio,
                imageParams,
                position,
            ),
        params,
    );
}

export function formatDirectBuilderImageUrl(
    url: string | undefined | null,
    width: number,
    params: string = "format=webp",
): string | undefined {
    return formatDirectImageUrl(url, `${params}&width=${width}`);
}

export function formatDirectBuilderImageUrlForAspectRatio(
    url: string | undefined | null,
    width: number,
    aspectRatio: number,
    params: string = "format=webp",
    position: string = BUILDER_IMAGE_DEFAULT_CROP_POSITION,
): string | undefined {
    return formatDirectImageUrlWithOverriddenParams(
        url,
        formatFixedAspectBuilderImageParams(width, aspectRatio, params, position),
    );
}

export function formatDirectBuilderImageSrcset(
    url: string | undefined | null,
    widths: readonly number[],
    params: string = "format=webp",
): string | undefined {
    return formatImageSrcsetWithFormatter(url, widths, formatDirectBuilderImageUrl, params);
}

export function formatDirectBuilderImageSrcsetForAspectRatio(
    url: string | undefined | null,
    widths: readonly number[],
    aspectRatio: number,
    params: string = "format=webp",
    position: string = BUILDER_IMAGE_DEFAULT_CROP_POSITION,
): string | undefined {
    return formatImageSrcsetWithFormatter(
        url,
        widths,
        (imageUrl, width, imageParams) =>
            formatDirectBuilderImageUrlForAspectRatio(
                imageUrl,
                width,
                aspectRatio,
                imageParams,
                position,
            ),
        params,
    );
}

export function formatTallBuilderImageUrl(
    url: string | undefined | null,
    width: number,
): string | undefined {
    return formatImageUrl(url, `quality=80&width=${width}&height=10000&fit=inside`);
}
